import { create } from "zustand";
import { nanoid } from "nanoid";

/**
 * ESG Settlement Ledger Store (v1)
 * 
 * Financial-grade sub-ledger that settles carbon allocations, offsets,
 * penalties and credit transfers between companies, subsidiaries, sectors and sovereign layers.
 */

export type SettlementScope = "SCOPE1" | "SCOPE2" | "SCOPE3" | "EMBODIED" | "ETS" | "SUBSIDY";

export interface SettlementEntry {
  id: string;
  ts: number;
  from?: string;
  to?: string;
  amount: number;
  scope: SettlementScope;
  reason: string;
  sovereignOverride?: boolean;
  meta?: any;
}

export interface PendingSettlementEntry extends SettlementEntry {
  queuedAt: number;
  retries?: number;
}

export interface SettlementBalances {
  [entity: string]: number;
}

export interface SettlementLedgerState {
  entries: SettlementEntry[];
  pendingQueue: PendingSettlementEntry[];
  balances: SettlementBalances;
  
  // Actions
  recordTransfer: (from: string, to: string, amount: number, reason: string, scope: SettlementScope, meta?: any) => void;
  applyPenalty: (entity: string, amount: number, code: string, meta?: any) => void;
  mintCredit: (entity: string, amount: number, meta?: any) => void;
  burnCredit: (entity: string, amount: number, meta?: any) => void;
  recalculateBalances: () => void;
  
  // Utility
  clearQueue: () => void;
  retryPending: () => void;
  createSnapshot: () => void;
  getSnapshot: (index: number) => SettlementEntry[] | null;
  rollbackToSnapshot: (index: number) => boolean;
  getSnapshots: () => Array<{ index: number; timestamp: number; entryCount: number }>;
  importEntries: (entries: SettlementEntry[], balances?: SettlementBalances) => void;
  
  // Aggregation
  getBalance: (entity: string) => number;
  aggregateByScope: () => Record<SettlementScope, number>;
}

const STORAGE_KEY = "evatledger:v1";
const SNAPSHOT_KEY = "evatledger:v1:snapshots";
const MAX_SNAPSHOTS = 50;

function generateId(): string {
  return nanoid();
}

function createSnapshot(entries: SettlementEntry[]): SettlementEntry[] {
  return JSON.parse(JSON.stringify(entries));
}

function loadSnapshots(): SettlementEntry[][] {
  try {
    const data = localStorage.getItem(SNAPSHOT_KEY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
}

function saveSnapshot(snapshots: SettlementEntry[][], newSnapshot: SettlementEntry[]): void {
  const updated = [newSnapshot, ...snapshots].slice(0, MAX_SNAPSHOTS);
  localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(updated));
}

export const useSettlementLedgerStore = create<SettlementLedgerState>((set, get) => {
  // Load from localStorage on init
  const loadPersisted = (): { entries: SettlementEntry[]; balances: SettlementBalances } => {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (data) {
        const parsed = JSON.parse(data);
        return {
          entries: parsed.entries || [],
          balances: parsed.balances || {},
        };
      }
    } catch {
      // Ignore
    }
    return { entries: [], balances: {} };
  };

  const persisted = loadPersisted();

  return {
      entries: persisted.entries,
      pendingQueue: [],
      balances: persisted.balances,

      recordTransfer: (from, to, amount, reason, scope, meta) => {
        const entry: SettlementEntry = {
          id: generateId(),
          ts: Date.now(),
          from,
          to,
          amount,
          scope,
          reason,
          meta,
        };
        
        set((state) => {
          const newEntries = [...state.entries, entry];
          const newState = { ...state, entries: newEntries };
          
          // Recalculate balances
          const balances = calculateBalances(newEntries);
          
          // Create snapshot
          const snapshots = loadSnapshots();
          saveSnapshot(snapshots, createSnapshot(newEntries));
          
          return { ...newState, entries: newEntries, balances };
        });
      },

      applyPenalty: (entity, amount, code, meta) => {
        const entry: SettlementEntry = {
          id: generateId(),
          ts: Date.now(),
          to: undefined,
          from: entity,
          amount: -amount, // Negative for penalty
          scope: "SUBSIDY" as SettlementScope,
          reason: `PENALTY:${code}`,
          meta: { ...meta, penaltyCode: code },
        };
        
        set((state) => {
          const newEntries = [...state.entries, entry];
          const balances = calculateBalances(newEntries);
          
          const snapshots = loadSnapshots();
          saveSnapshot(snapshots, createSnapshot(newEntries));
          
          return { entries: newEntries, balances };
        });
      },

      mintCredit: (entity, amount, meta) => {
        const entry: SettlementEntry = {
          id: generateId(),
          ts: Date.now(),
          from: undefined,
          to: entity,
          amount,
          scope: "SUBSIDY" as SettlementScope,
          reason: "MINT",
          sovereignOverride: true,
          meta,
        };
        
        set((state) => {
          const newEntries = [...state.entries, entry];
          const balances = calculateBalances(newEntries);
          
          const snapshots = loadSnapshots();
          saveSnapshot(snapshots, createSnapshot(newEntries));
          
          return { entries: newEntries, balances };
        });
      },

      burnCredit: (entity, amount, meta) => {
        const entry: SettlementEntry = {
          id: generateId(),
          ts: Date.now(),
          from: entity,
          to: undefined,
          amount: -amount,
          scope: "SUBSIDY" as SettlementScope,
          reason: "BURN",
          meta,
        };
        
        set((state) => {
          const newEntries = [...state.entries, entry];
          const balances = calculateBalances(newEntries);
          
          const snapshots = loadSnapshots();
          saveSnapshot(snapshots, createSnapshot(newEntries));
          
          return { entries: newEntries, balances };
        });
      },

      recalculateBalances: () => {
        const { entries } = get();
        const balances = calculateBalances(entries);
        set({ balances });
      },

      clearQueue: () => {
        set({ pendingQueue: [] });
      },

      retryPending: () => {
        const { pendingQueue, entries } = get();
        // Move pending entries to main entries (simplified - in production would check server status)
        const newEntries = [...entries, ...pendingQueue.map(q => ({
          id: q.id,
          ts: q.ts,
          from: q.from,
          to: q.to,
          amount: q.amount,
          scope: q.scope,
          reason: q.reason,
          sovereignOverride: q.sovereignOverride,
          meta: q.meta,
        }))];
        const balances = calculateBalances(newEntries);
        set({ entries: newEntries, pendingQueue: [], balances });
      },

      createSnapshot: () => {
        const { entries } = get();
        const snapshots = loadSnapshots();
        saveSnapshot(snapshots, createSnapshot(entries));
      },

      getSnapshot: (index) => {
        const snapshots = loadSnapshots();
        return snapshots[index] || null;
      },

      rollbackToSnapshot: (index) => {
        const snapshots = loadSnapshots();
        const snapshot = snapshots[index];
        if (!snapshot) return false;
        
        set((state) => {
          const balances = calculateBalances(snapshot);
          return { entries: snapshot, balances };
        });
        
        return true;
      },

      getSnapshots: () => {
        const snapshots = loadSnapshots();
        return snapshots.map((snapshot, index) => ({
          index,
          timestamp: snapshot.length > 0 ? snapshot[snapshot.length - 1].ts : Date.now(),
          entryCount: snapshot.length,
        }));
      },

      importEntries: (entries, balances) => {
        const calculatedBalances = balances || calculateBalances(entries);
        set({ entries, balances: calculatedBalances });
        
        // Create snapshot of imported state
        const snapshots = loadSnapshots();
        saveSnapshot(snapshots, createSnapshot(entries));
      },

      getBalance: (entity) => {
        const { balances } = get();
        return balances[entity] || 0;
      },

      aggregateByScope: () => {
        const { entries } = get();
        const aggregation: Record<SettlementScope, number> = {
          SCOPE1: 0,
          SCOPE2: 0,
          SCOPE3: 0,
          EMBODIED: 0,
          ETS: 0,
          SUBSIDY: 0,
        };
        
        entries.forEach((entry) => {
          aggregation[entry.scope] = (aggregation[entry.scope] || 0) + entry.amount;
        });
        
        return aggregation;
      },
    };
  }
);

// Persist to localStorage on state changes
useSettlementLedgerStore.subscribe((state) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      entries: state.entries,
      balances: state.balances,
    }));
  } catch {
    // Ignore localStorage errors
  }
});

/**
 * Calculate balances from entries (deterministic, idempotent)
 */
function calculateBalances(entries: SettlementEntry[]): SettlementBalances {
  const balances: SettlementBalances = {};
  
  entries.forEach((entry) => {
    let amount = entry.amount;
    
    // Apply sovereign override multiplier (1.1x for penalties)
    if (entry.sovereignOverride && entry.amount < 0) {
      amount = entry.amount * 1.1;
    }
    
    // Apply scope multipliers
    if (entry.scope === "EMBODIED") {
      amount = amount * 1.02;
    } else if (entry.scope === "ETS") {
      // ETS multiplier from env (default 1.05)
      amount = amount * 1.05;
    }
    
    // Apply to 'from' entity (subtract)
    if (entry.from) {
      balances[entry.from] = (balances[entry.from] || 0) - amount;
    }
    
    // Apply to 'to' entity (add)
    if (entry.to) {
      balances[entry.to] = (balances[entry.to] || 0) + amount;
    }
  });
  
  return balances;
}

// Alias for specification compatibility
export const useSettlementLedger = useSettlementLedgerStore;

